import { useCallback, useEffect, useState } from "react";
import { useWindowSize } from "@infrastructure/Hooks/useWindowSize";
import useWindowScroll from "react-use/lib/useWindowScroll";
import { useModalsStateStore } from "@infrastructure/Persistence/ModalsStateStore";
import { addPxPostfix } from "@shared/addPxPostfix";

/**
 * Hook to manage scrolling functionality during modals (no background scrolling)
 * Uses document.body as a synchronous store
 * In case of switching modals one modal needs to be held open before close it
 * to correctly calculate offset
 */
export const useBodyScrollModalControlState = (
    isOpen: boolean,
    trackDocumentTop: boolean = true,
) => {
    const [modalId] = useState(Math.random());

    const setPrevPageY = useCallback((value: number) => {
        document.body.dataset.y = value.toString();
    }, []);

    const getPrevPageY = useCallback(() => {
        return Number(document.body.dataset.y) ?? 0;
    }, []);

    const { addModalToOpenModalsArray, removeModalFromOpenModalsArray } =
        useModalsStateStore();

    useEffect(() => {
        if (isOpen) {
            addModalToOpenModalsArray(modalId.toString());
        } else {
            removeModalFromOpenModalsArray(modalId.toString());
        }
        return () => {
            removeModalFromOpenModalsArray(modalId.toString());
        };
    }, [isOpen]);

    const { isMobileScreenSizeFlag: enabled } = useWindowSize();
    const { y: pageY } = useWindowScroll();
    useEffect(() => {
        if (!enabled) {
            return;
        }
        if (isOpen) {
            try {
                const activeModals: number[] = JSON.parse(
                    document.body.dataset?.activeModals ?? "[]",
                );
                if (pageY !== 0 && trackDocumentTop) {
                    document.body.style.top = addPxPostfix(pageY * -1);
                    setPrevPageY(pageY);
                }
                document.body.style.position = "fixed";
                document.body.style.overflow = "hidden";
                if (!activeModals.includes(modalId)) {
                    activeModals.push(modalId);
                    document.body.dataset.activeModals =
                        JSON.stringify(activeModals);
                }
            } catch (e) {
                return;
            }
        } else {
            try {
                cleanup();
                const activeModals: number[] = JSON.parse(
                    document.body?.dataset.activeModals ?? "[]",
                );
                const prevPageY = getPrevPageY();
                if (
                    prevPageY &&
                    activeModals.length === 0 &&
                    trackDocumentTop
                ) {
                    window.scrollTo({
                        top: prevPageY,
                    });
                    setPrevPageY(0);
                }
            } catch (e) {
                return;
            }
        }
        return cleanup;
    }, [isOpen]);

    const cleanup = () => {
        try {
            const activeModals: number[] = JSON.parse(
                document.body?.dataset.activeModals ?? "[]",
            );
            const index = activeModals.indexOf(modalId);
            if (index !== -1 && document.body?.dataset) {
                activeModals.splice(index, 1);
                document.body.dataset.activeModals =
                    JSON.stringify(activeModals);
            }
            if (activeModals.length !== 0) {
                return;
            }

            if (document.body?.style) {
                document.body.style?.removeProperty?.("overflow");
                document.body.style?.removeProperty?.("position");
                if (trackDocumentTop) {
                    document.body.style?.removeProperty?.("top");
                }
            }

            delete document.body.dataset.activeModals;
        } catch (e) {
            return;
        }
    };

    return modalId;
};
